import React, { useState, useMemo } from "react";
import { Box, IconButton } from "@mui/material";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import {
  Alert,
  Button,
  DataTableV2,
  useAgGridOptions,
} from "@platform/design-system";

import { Loader } from "../../../components";
import { useAnalystTeams } from "../../../hooks";
import { AnalystTeam } from "../types";

import { TeamForm } from "./TeamForm";

interface EditTeamContext {
  onEditTeam: (team: AnalystTeam) => void;
}

const EditTeamCellRenderer: React.FC<ICellRendererParams<AnalystTeam>> = (
  params,
) => {
  const context = params.context as EditTeamContext | undefined;

  const handleClick = (): void => {
    if (!context?.onEditTeam || !params.data) return;
    context.onEditTeam(params.data);
  };

  return (
    <IconButton
      size="small"
      onClick={handleClick}
      color="primary"
      sx={{ color: "text.primary" }}
    >
      <EditIcon />
    </IconButton>
  );
};

export const TeamsTab: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<AnalystTeam | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { data: teamsResponse, isLoading, error, refetch } = useAnalystTeams();

  const teams = (teamsResponse?.teams ?? []) as AnalystTeam[];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { resetPage, ...dataTableOptions } = useAgGridOptions({});

  const columnDefs = useMemo<ColDef<AnalystTeam>[]>(
    () => [
      { field: "name", headerName: "Team Name" },
      { field: "description", headerName: "Description" },
      { field: "teamLeadEmail", headerName: "Team Lead Email" },
      {
        headerName: "Actions",
        cellRenderer: EditTeamCellRenderer,
        sortable: false,
        filter: false,
        maxWidth: 100,
      },
    ],
    [],
  );

  const handleAddTeam = (): void => {
    setEditingTeam(null);
    setIsFormOpen(true);
  };

  const handleEditTeam = (team: AnalystTeam): void => {
    setEditingTeam(team);
    setIsFormOpen(true);
  };

  const handleCloseForm = (): void => {
    setIsFormOpen(false);
    setEditingTeam(null);
  };

  const handleFormSuccess = (): void => {
    void refetch();
    handleCloseForm();
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">
          Failed to load teams:{" "}
          {error instanceof Error ? error.message : "Unknown error"}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="primary"
          startIcon={<AddIcon />}
          onClick={handleAddTeam}
        >
          Add New Team
        </Button>
      </Box>

      <DataTableV2
        rowData={teams}
        columnDefs={columnDefs}
        context={{ onEditTeam: handleEditTeam }}
        {...dataTableOptions}
      />

      <TeamForm
        open={isFormOpen}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        team={editingTeam}
      />
    </>
  );
};
